'use client';

import { useState, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';

interface FilterOption {
  value: string;
  label: string;
}

interface FilterDropdownProps {
  label: string;
  options: FilterOption[];
  selectedValues?: string[];
  onSelect: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  searchable?: boolean;
  disabled?: boolean;
  singleSelect?: boolean; // Tekli seçim için
}

export default function FilterDropdown({
  label,
  options,
  selectedValues = [],
  onSelect,
  // placeholder = "Seçiniz...",
  className = "",
  searchable = false,
  disabled = false,
  singleSelect = false
}: FilterDropdownProps) {
  const t = useTranslations('auctions.filters');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>('left');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDropdownToggle = () => {
    if (disabled) return; // Disabled ise açılmasın
    
    if (!isOpen) {
      // Dropdown açılırken pozisyonu hesapla - requestAnimationFrame ile forced reflow'u önle
      if (dropdownRef.current) {
        requestAnimationFrame(() => {
          if (dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const dropdownWidth = 250; // Tahmini dropdown genişliği
            
            if (rect.left + dropdownWidth > viewportWidth - 20) {
              setDropdownPosition('right');
            } else {
              setDropdownPosition('left');
            }
          }
        });
      }
    }
    setIsOpen(!isOpen);
  };

  const handleOptionToggle = (value: string) => {
    if (singleSelect) {
      // Tekli seçim: Seçili değer zaten seçiliyse temizle, değilse sadece bu değeri seç
      if (selectedValues.includes(value)) {
        // Aynı değere tekrar tıklandığında seçimi temizle (dropdown açık kalsın)
        onSelect([]);
        // Dropdown'ı açık tut, kullanıcı seçimi iptal etti
      } else {
        // Yeni değer seçildiğinde sadece bu değeri seç (önceki seçimi kaldır)
        onSelect([value]);
        setIsOpen(false); // Tekli seçimde yeni seçim yapınca dropdown'ı kapat
      }
    } else {
      // Çoklu seçim: Mevcut mantık
      const newSelectedValues = selectedValues.includes(value)
        ? selectedValues.filter(v => v !== value)
        : [...selectedValues, value];
      
      onSelect(newSelectedValues);
    }
  };

  const filteredOptions = searchable 
    ? options.filter(option => 
        option.label.toLocaleLowerCase('tr-TR').includes(searchTerm.toLocaleLowerCase('tr-TR'))
      )
    : options;

  const getDisplayText = () => {
    if (selectedValues.length === 0) return label;
    if (selectedValues.length === 1) {
      const option = options.find(opt => opt.value === selectedValues[0]);
      return option ? option.label : label;
    }
    if (selectedValues.length <= 3) {
      // 3 veya daha az seçili ise isimlerini göster
      const selectedLabels = selectedValues
        .map(value => options.find(opt => opt.value === value)?.label)
        .filter(Boolean);
      return selectedLabels.join(', ');
    }
    // 3'ten fazla seçili ise sayı + ilk 2 ismi göster
    const selectedLabels = selectedValues
      .map(value => options.find(opt => opt.value === value)?.label)
      .filter(Boolean);
    return `${selectedLabels.slice(0, 2).join(', ')} +${selectedValues.length - 2}`;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={handleDropdownToggle}
        disabled={disabled}
        className={`flex items-center justify-between w-full px-3 sm:px-6 py-2 sm:py-3 bg-white border border-gray-200 rounded-full shadow-sm transition-shadow ${
          disabled 
            ? 'opacity-50 cursor-not-allowed' 
            : 'hover:shadow-md cursor-pointer'
        }`}
      >
        <span className="text-sm sm:text-base font-semibold text-gray-900 truncate">
          {getDisplayText()}
        </span>
        <svg
          className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-600 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={`absolute top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto w-full min-w-[200px] sm:min-w-[250px] max-w-[300px] sm:max-w-[350px] ${
          dropdownPosition === 'right' ? 'right-0' : 'left-0'
        }`}>
          {searchable && (
            <div className="sticky top-0 bg-white border-b border-gray-100 p-2 sm:p-3">
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 sm:px-3 py-2 border border-gray-200 rounded-md text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          )}
          
          {filteredOptions.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            
            // Tekli seçim için text only, çoklu seçim için checkbox
            if (singleSelect) {
              return (
                <div
                  key={option.value}
                  onClick={() => handleOptionToggle(option.value)}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-3 transition-colors cursor-pointer ${
                    isSelected
                      ? 'bg-red-50 text-red-600 font-semibold'
                      : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <span className="text-sm sm:text-base whitespace-nowrap">
                    {option.label}
                  </span>
                </div>
              );
            } else {
              // Çoklu seçim için checkbox
              return (
                <label
                  key={option.value}
                  className="flex items-center w-full px-3 sm:px-4 py-2 sm:py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleOptionToggle(option.value)}
                    className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 border-gray-300 rounded focus:outline-none"
                  />
                  <span className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-900 whitespace-nowrap">
                    {option.label}
                  </span>
                </label>
              );
            }
          })}
          
          {filteredOptions.length === 0 && searchable && (
            <div className="px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-gray-500">
              {t('noResults')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}